/**
 * MaizeOverviewTab — Monthly trends, top farmers, "how it works" explainer.
 * Pure presentational — receives summary data via props.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import { Users, Wheat, ShoppingCart, TrendingUp } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../lib/utils';

const TOOLTIP_STYLE = { borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' };

const MaizeOverviewTab = ({ summary }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Monthly Trends */}
      <Card className="card-hover" data-testid="maize-trends-chart">
        <CardHeader>
          <CardTitle className="font-heading">Monthly Activity</CardTitle>
          <CardDescription>Purchases vs Sales over time</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={summary?.monthly_trends || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  formatter={(value, name) => [
                    name.includes('kg') ? `${formatNumber(value)} kg` : formatCurrency(value),
                    name,
                  ]}
                  contentStyle={TOOLTIP_STYLE}
                />
                <Legend />
                <Bar dataKey="purchased_kg" name="Purchased (kg)" fill="#2E7D32" radius={[4, 4, 0, 0]} />
                <Bar dataKey="sold_kg" name="Sold (kg)" fill="#1E88E5" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Top Farmers */}
      <Card className="card-hover" data-testid="top-farmers-chart">
        <CardHeader>
          <CardTitle className="font-heading">Top Supplying Farmers</CardTitle>
          <CardDescription>Women farmers with highest contributions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={summary?.top_farmers || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tickFormatter={(v) => `${v}kg`} />
                <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(value) => [`${formatNumber(value)} kg`, 'Supplied']}
                  contentStyle={TOOLTIP_STYLE}
                />
                <Bar dataKey="kg" fill="#F9A825" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>

    {/* How It Works */}
    <Card className="bg-gradient-to-r from-amber-50 to-green-50 dark:from-amber-900/10 dark:to-green-900/10">
      <CardHeader>
        <CardTitle className="font-heading">How the Program Works</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Step icon={Users} color="amber" title="1. Train Women" body="We train women in commercial maize farming techniques" />
          <Step icon={Wheat} color="green" title="2. They Grow" body="Farmers grow maize on their land with our support" />
          <Step icon={ShoppingCart} color="blue" title="3. We Buy" body="Suubi buys maize at fair prices they can't get elsewhere" />
          <Step icon={TrendingUp} color="purple" title="4. Strategic Sale" body="We store and sell at optimal times for sustainability" />
        </div>
      </CardContent>
    </Card>
  </div>
);

const Step = ({ icon: Icon, color, title, body }) => (
  <div className="text-center">
    <div className={`w-12 h-12 bg-${color}-100 rounded-full flex items-center justify-center mx-auto mb-3`}>
      <Icon className={`h-6 w-6 text-${color}-600`} />
    </div>
    <h4 className="font-medium mb-1">{title}</h4>
    <p className="text-sm text-muted-foreground">{body}</p>
  </div>
);

export default MaizeOverviewTab;
